import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { EventInfo } from '@/api/events/types/types';
import PlayerImagePlaceholderMasked from '@/assets/images/playerImagePlaceholderMasked';
import { Text } from '@/components/TextComponent';
import { Box, Row, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { playerPropsSelector, usePlayerPropsStore } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { UfcGradient } from '@/feature/fantasy-event-sheets/components/ufc-events/UfcGradient';
import { EventWidgetImageContainer } from '@/feature/player-profile-sheets/components/EventWidgetImageContainer';
import { getPlayerNameInfo } from '@/feature/player-profile-sheets/utils/player-profile-utils';
import { IndividualVersusEvent, Player } from '@/types/api.generated';
import { MAX_WEB_WIDTH, isWeb } from '@/utils/constants-platform-specific';
import { isPlayerImgPlaceholder } from '@/utils/player';

type UfcFightRowProps = {
    eventInfo: IndividualVersusEvent;
    openPlayerProfile: (playerId: string, eventId: string) => void;
};

export const UfcFightRow = ({ eventInfo, openPlayerProfile }: UfcFightRowProps) => {
    const [leftPlayer, rightPlayer] = eventInfo.players;

    return (
        <Row
            marginHorizontal={'s16'}
            marginTop={'s12'}
            overflow={'hidden'}
            maxWidth={MAX_WEB_WIDTH}
            width={isWeb ? '100%' : 'auto'}
            alignSelf={'center'}
        >
            <UfcPlayerCard
                event={eventInfo}
                player={leftPlayer}
                alignment="left"
                openPressPlayer={() => openPlayerProfile(leftPlayer.id, eventInfo.id)}
            />
            <VsDot />
            <UfcPlayerCard
                event={eventInfo}
                player={rightPlayer}
                alignment="right"
                openPressPlayer={() => openPlayerProfile(rightPlayer.id, eventInfo.id)}
            />
        </Row>
    );
};

const UfcPlayerCard = ({
    player,
    event,
    alignment = 'left',
    openPressPlayer,
}: {
    player: Player;
    event: EventInfo;
    alignment?: 'left' | 'right';
    openPressPlayer: () => void;
}) => {
    const isLeftAlignment = alignment === 'left';

    const isUnavailable = player === undefined || player === null;

    const isPlaceholderImage = isPlayerImgPlaceholder(player?.icon);

    const { mainName } = getPlayerNameInfo(player);

    return (
        <TouchableBox flex={1} onPress={openPressPlayer} disabled={isUnavailable} opacity={isUnavailable ? 0.5 : 1}>
            <Box
                style={[styles.playerBox, isLeftAlignment ? styles.leftPlayerBox : styles.rightPlayerBox]}
                borderTopLeftRadius={'r16'}
                borderBottomLeftRadius={'r16'}
                borderTopRightRadius={'r2'}
                borderBottomRightRadius={'r2'}
                overflow={'hidden'}
                flexDirection={isLeftAlignment ? 'row' : 'row-reverse'}
                gap={'s6'}
            >
                {isUnavailable ? null : <UfcGradient alignment={alignment} />}

                {isPlaceholderImage ? (
                    <PlayerImagePlaceholderMasked id={player?.id} opacity={0.4} />
                ) : (
                    <EventWidgetImageContainer
                        height={44}
                        width={44}
                        shouldApplyMask
                        imageUrl={player?.icon}
                        maskOffset={7.7}
                    />
                )}
                <Box alignItems={isLeftAlignment ? 'flex-start' : 'flex-end'} justifyContent={'center'}>
                    {isUnavailable ? (
                        <Text variant={'bodyMedium'} color={'neutral300'}>
                            TBD
                        </Text>
                    ) : (
                        <>
                            <Text color={ds.colorToken.text.primary}>{mainName}</Text>
                            <UfcProjectionDetails player={player} event={event} />
                        </>
                    )}
                </Box>
            </Box>
        </TouchableBox>
    );
};

const PLAYER_HEIGHT = 68;
const BORDER_WIDTH = 1;

const UfcProjectionDetails = ({ player, event }: { player: Player; event: EventInfo }) => {
    const { t } = useTranslation('betslip_pickem');

    const playerProjectionsLength = usePlayerPropsStore(
        state => playerPropsSelector(player.id, event.id)(state).length
    );
    return (
        <Text variant="bodySmall" numberOfLines={1} lineHeight={20} letterSpacing={-0.08} color="neutral300">
            {`${t('projection', { count: playerProjectionsLength })}`}
        </Text>
    );
};

const styles = StyleSheet.create({
    playerBox: {
        height: PLAYER_HEIGHT,
        flex: 1,
        backgroundColor: ds.colorValue.background.neutral.subtler,
        paddingVertical: 12 - BORDER_WIDTH,
        borderWidth: BORDER_WIDTH,
        borderStyle: 'solid',
        borderColor: ds.colorValue.border.strong,
        paddingHorizontal: 8,
        overflow: 'hidden',
    },
    vsDot: {
        marginLeft: -12,
        borderRadius: 14,
        borderWidth: BORDER_WIDTH,
        borderColor: ds.colorValue.border.strong,
        borderStyle: 'solid',
    },
    leftPlayerBox: {
        borderTopLeftRadius: 16,
        borderBottomLeftRadius: 16,
        borderTopRightRadius: 2,
        borderBottomRightRadius: 2,
        flexDirection: 'row',
    },
    rightPlayerBox: {
        borderTopLeftRadius: 2,
        borderBottomLeftRadius: 2,
        borderTopRightRadius: 16,
        borderBottomRightRadius: 16,
        flexDirection: 'row-reverse',
    },
});
const VsDot = () => (
    <Box width={4} zIndex={1} alignContent={'center'} justifyContent={'center'}>
        <Box
            width={28}
            height={28}
            style={styles.vsDot}
            alignContent={'center'}
            justifyContent={'center'}
            backgroundColor={ds.colorToken.background.neutral.base}
        >
            <Box
                position={'absolute'}
                left={0}
                right={0}
                top={0}
                bottom={0}
                alignItems={'center'}
                justifyContent={'center'}
            >
                <Box width={4} height={36} backgroundColor={ds.colorToken.background.neutral.base} />
            </Box>
            <Text textAlign={'center'} variant={'labelLarge'} fontSize={13} letterSpacing={-0.08} color="neutral300">
                vs
            </Text>
        </Box>
    </Box>
);
